import { Routes, Route, Navigate } from 'react-router-dom'
import { useAuth } from './hooks/useAuth'
import { ProtectedRoute } from './components/ProtectedRoute'
import { PageProtectedRoute } from './components/PageProtectedRoute'
import { ErrorBoundary } from './components/ErrorBoundary'
import AdminLayout from './layouts/AdminLayout'
import Loader from './components/Loader'
import { LoginPage } from './pages/LoginPage'
import { DashboardPage } from './pages/DashboardPage'
import { SparMobilePage } from './pages/SparMobilePage'
import { ProductsPage } from './pages/ProductsPage'
import { InnerboxPage } from './pages/InnerboxPage'
import { WalletPage } from './pages/WalletPage'
import { WalletFailuresPage } from './pages/WalletFailuresPage'
import { EmailHistoryPage } from './pages/EmailHistoryPage'
import { BrevoContactsSyncPage } from './pages/BrevoContactsSyncPage'
import { AbuseDetectionReportsPage } from './pages/AbuseDetectionReportsPage'
import { SimImportPage } from './pages/SimImportPage'
import { AssignEsimsPage } from './pages/AssignEsimsPage'
import { SimStockPage } from './pages/SimStockPage'
import { HoneyConnectPage } from './pages/HoneyConnectPage'
import { TransactionsPage } from './pages/TransactionsPage'
import { CustomersPage } from './pages/CustomersPage'
import { UserManagementPage } from './pages/UserManagementPage'
import { RolePermissionsPage } from './pages/RolePermissionsPage'
import { AiSqlPage } from './pages/AiSqlPage'
import { DistributorsPage } from './pages/DistributorsPage'
import { DistributorVerificationPage } from './pages/DistributorVerificationPage'
import { SubDistributorVerificationFormPage } from './pages/SubDistributorVerificationFormPage'
import { EntitiesPage } from './pages/EntitiesPage'
import { PeoplePage } from './pages/PeoplePage'
import { LegalEntityDetailPage } from './pages/LegalEntityDetailPage'
import { EasyCronPage } from './pages/EasyCronPage'
import { JobsPage } from './pages/JobsPage'
import { Site24x7Page } from './pages/Site24x7Page'
import { DmaMonitorPage } from './pages/DmaMonitorPage'
import { PubsubPage } from './pages/PubsubPage'
import { ColumnToCsvPage } from './pages/ColumnToCsvPage'
import { CsvToJsonPage } from './pages/CsvToJsonPage'
import { AgentSyncPage } from './pages/AgentSyncPage'
import { MtnBillingReconPage } from './pages/MtnBillingReconPage'
import { MtnInvoicesPage } from './pages/MtnInvoicesPage'
import { ShopifyStockManagementPage } from './pages/ShopifyStockManagementPage'
import { ShopifyMonthlyBillingPage } from './pages/ShopifyMonthlyBillingPage'
import { ManageSurveysPage } from './pages/ManageSurveysPage'
import { CreateEditSurveyPage } from './pages/surveys/CreateEditSurveyPage'
import { SurveyDetailPage } from './pages/SurveyDetailPage'
import { ReportsPage } from './pages/ReportsPage'
import EntityCompletePage from './pages/EntityCompletePage'
import { XeroContactsPage } from './pages/XeroContactsPage'
import { XeroStatementsPage } from './pages/XeroStatementsPage'
import { CampaignsPage } from './pages/CampaignsPage'
import { CampaignStatsPage } from './pages/CampaignStatsPage'
import { CampaignJobsPage } from './pages/CampaignJobsPage'
import { TestRecipientsPage } from './pages/TestRecipientsPage'
import { DirectSendPage } from './pages/DirectSendPage'
import { ScheduledCampaignsPage } from './pages/ScheduledCampaignsPage'
import { IotPage } from './pages/IotPage'
import { IotCustomerAccountsPage } from './pages/IotCustomerAccountsPage'
import { OttVoucherPage } from './pages/OttVoucherPage'
import { ChurnPage } from './pages/ChurnPage'
import { ChurnFailedExecutionsPage } from './pages/ChurnFailedExecutionsPage'
import { BasketSearchPage } from './pages/BasketSearchPage'
import { TransferPromoDataPage } from './pages/TransferPromoDataPage'
import { MerchantAccountsPage } from './pages/MerchantAccountsPage'
import { PromoAttributionOverviewPage } from './pages/PromoAttributionOverviewPage'
import { PromoAttributionExecutiveSummaryPage } from './pages/PromoAttributionExecutiveSummaryPage'
import { PromoAttributionProductsPage } from './pages/PromoAttributionProductsPage'
import { PromoAttributionProductDetailPage } from './pages/PromoAttributionProductDetailPage'
import { PromoAttributionRulesPage } from './pages/PromoAttributionRulesPage'
import { PromoAttributionMethodologyPage } from './pages/PromoAttributionMethodologyPage'

function App() {
  const { isLoading } = useAuth()

  if (isLoading) {
    return <Loader />
  }

  return (
    <Routes>
      <Route path="/login" element={<LoginPage />} />
      {/* Public route for external clients to complete entity details */}
      <Route path="compliance/entities/:entityId/complete" element={<EntityCompletePage />} />
      <Route
        path="/"
        element={
          <ProtectedRoute>
            <AdminLayout />
          </ProtectedRoute>
        }
      >
        <Route index element={<Navigate to="/dashboard" replace />} />
        <Route path="dashboard" element={<PageProtectedRoute pageKey="dashboard"><DashboardPage /></PageProtectedRoute>} />
        <Route path="customers" element={<PageProtectedRoute pageKey="customers"><ErrorBoundary><CustomersPage /></ErrorBoundary></PageProtectedRoute>} />
        <Route path="products" element={<PageProtectedRoute pageKey="products"><ProductsPage /></PageProtectedRoute>} />
        <Route path="distributors" element={<PageProtectedRoute pageKey="distributors"><DistributorsPage /></PageProtectedRoute>} />
        <Route path="spar-mobile/store-compliance" element={<PageProtectedRoute pageKey="spar-mobile"><DistributorVerificationPage /></PageProtectedRoute>} />
        <Route path="spar-mobile/store-compliance/complete/:distributorICustomer/:subDistributorId" element={<PageProtectedRoute pageKey="spar-mobile"><SubDistributorVerificationFormPage /></PageProtectedRoute>} />
        <Route path="spar-mobile/store-compliance/complete" element={<PageProtectedRoute pageKey="spar-mobile"><SubDistributorVerificationFormPage /></PageProtectedRoute>} />
        <Route path="compliance/entities" element={<PageProtectedRoute pageKey="entities"><EntitiesPage /></PageProtectedRoute>} />
        <Route path="compliance/entities/:entityId" element={<PageProtectedRoute pageKey="entities"><LegalEntityDetailPage /></PageProtectedRoute>} />
        <Route path="compliance/people" element={<PageProtectedRoute pageKey="people"><PeoplePage /></PageProtectedRoute>} />
        <Route path="sim-cards/innerbox" element={<PageProtectedRoute pageKey="innerbox"><InnerboxPage /></PageProtectedRoute>} />
        <Route path="wallet" element={<PageProtectedRoute pageKey="wallet"><WalletPage /></PageProtectedRoute>} />
        <Route path="wallet-failures" element={<PageProtectedRoute pageKey="wallet"><WalletFailuresPage /></PageProtectedRoute>} />
        <Route path="wallet/merchant-accounts" element={<PageProtectedRoute pageKey="wallet"><MerchantAccountsPage /></PageProtectedRoute>} />
        <Route path="email-history" element={<PageProtectedRoute pageKey="email-history"><EmailHistoryPage /></PageProtectedRoute>} />
        <Route
          path="sim-cards"
          element={
            <PageProtectedRoute pageKey="sim-cards">
              <Navigate to="/sim-cards/assign-esims" replace />
            </PageProtectedRoute>
          }
        />
        <Route path="sim-cards/sim-import" element={<PageProtectedRoute pageKey="sim-import"><SimImportPage /></PageProtectedRoute>} />
        <Route path="sim-cards/sim-stock" element={<PageProtectedRoute pageKey="sim-stock"><SimStockPage /></PageProtectedRoute>} />
        <Route path="sim-cards/assign-esims" element={<PageProtectedRoute pageKey="assign-esims"><AssignEsimsPage /></PageProtectedRoute>} />
        <Route
          path="cycle"
          element={
            <PageProtectedRoute pageKey="cycle">
              <Navigate to="/cycle/agent-sync" replace />
            </PageProtectedRoute>
          }
        />
        <Route
          path="cycle/agent-sync"
          element={
            <PageProtectedRoute pageKey="cycle">
              <AgentSyncPage />
            </PageProtectedRoute>
          }
        />
        <Route path="honey-connect" element={<PageProtectedRoute pageKey="honey-connect"><HoneyConnectPage /></PageProtectedRoute>} />
        <Route path="transactions" element={<PageProtectedRoute pageKey="transactions"><TransactionsPage /></PageProtectedRoute>} />
        <Route
          path="promo-data"
          element={
            <PageProtectedRoute pageKey="promo-data">
              <Navigate to="/promo-data/rewards-engine" replace />
            </PageProtectedRoute>
          }
        />
        <Route
          path="promo-data/rewards-engine"
          element={
            <PageProtectedRoute pageKey="promo-data">
              <SparMobilePage basePath="/promo-data" sectionName="rewards-engine" />
            </PageProtectedRoute>
          }
        />
        <Route
          path="campaigns"
          element={
            <PageProtectedRoute pageKey="campaigns">
              <CampaignsPage />
            </PageProtectedRoute>
          }
        />
        <Route
          path="campaigns/stats"
          element={
            <PageProtectedRoute pageKey="campaigns">
              <CampaignStatsPage />
            </PageProtectedRoute>
          }
        />
        <Route
          path="campaigns/jobs"
          element={
            <PageProtectedRoute pageKey="campaigns">
              <CampaignJobsPage />
            </PageProtectedRoute>
          }
        />
        <Route
          path="campaigns/test-recipients"
          element={
            <PageProtectedRoute pageKey="campaigns">
              <TestRecipientsPage />
            </PageProtectedRoute>
          }
        />
        <Route
          path="campaigns/direct-send"
          element={
            <PageProtectedRoute pageKey="campaigns">
              <DirectSendPage />
            </PageProtectedRoute>
          }
        />
        <Route
          path="campaigns/scheduled"
          element={
            <PageProtectedRoute pageKey="campaigns">
              <ScheduledCampaignsPage />
            </PageProtectedRoute>
          }
        />
        <Route
          path="monitoring"
          element={
            <PageProtectedRoute pageKey="monitoring">
              <Navigate to="/monitoring/easycron" replace />
            </PageProtectedRoute>
          }
        />
        <Route
          path="monitoring/easycron"
          element={
            <PageProtectedRoute pageKey="monitoring">
              <EasyCronPage />
            </PageProtectedRoute>
          }
        />
        <Route
          path="monitoring/site24x7"
          element={
            <PageProtectedRoute pageKey="monitoring">
              <Site24x7Page />
            </PageProtectedRoute>
          }
        />
        <Route
          path="monitoring/dma-monitor"
          element={
            <PageProtectedRoute pageKey="monitoring">
              <DmaMonitorPage />
            </PageProtectedRoute>
          }
        />
        <Route
          path="monitoring/pubsub"
          element={
            <PageProtectedRoute pageKey="monitoring">
              <PubsubPage />
            </PageProtectedRoute>
          }
        />
        <Route path="jobs" element={<PageProtectedRoute pageKey="jobs"><JobsPage /></PageProtectedRoute>} />
        <Route
          path="monitoring/jobs"
          element={<Navigate to="/jobs" replace />}
        />
        <Route
          path="spar-promo"
          element={
            <PageProtectedRoute pageKey="spar-mobile">
              <Navigate to="/spar-mobile/incentive-reconciliation" replace />
            </PageProtectedRoute>
          }
        />
        <Route
          path="spar-promo/:section"
          element={
            <PageProtectedRoute pageKey="spar-mobile">
              <SparMobilePage basePath="/spar-promo" />
            </PageProtectedRoute>
          }
        />
        <Route
          path="spar-mobile/incentive-reconciliation"
          element={
            <PageProtectedRoute pageKey="spar-mobile">
              <SparMobilePage basePath="/spar-mobile" sectionName="incentive-reconciliation" />
            </PageProtectedRoute>
          }
        />
        <Route
          path="spar-mobile/spar-rewards"
          element={
            <PageProtectedRoute pageKey="spar-mobile">
              <SparMobilePage basePath="/spar-mobile" sectionName="spar-rewards" />
            </PageProtectedRoute>
          }
        />
        <Route
          path="spar-mobile/store-management"
          element={
            <PageProtectedRoute pageKey="spar-mobile">
              <Navigate to="/spar-mobile/rom-management" replace />
            </PageProtectedRoute>
          }
        />
        <Route
          path="spar-mobile/rom-management"
          element={
            <PageProtectedRoute pageKey="spar-mobile">
              <SparMobilePage basePath="/spar-mobile" sectionName="rom-management" />
            </PageProtectedRoute>
          }
        />
        <Route
          path="spar-mobile/store-ownership-change"
          element={
            <PageProtectedRoute pageKey="spar-mobile">
              <SparMobilePage basePath="/spar-mobile" sectionName="store-ownership-change" />
            </PageProtectedRoute>
          }
        />
        <Route
          path="spar-mobile/voucher-matching"
          element={
            <PageProtectedRoute pageKey="spar-mobile">
              <SparMobilePage basePath="/spar-mobile" sectionName="voucher-matching" />
            </PageProtectedRoute>
          }
        />
        <Route
          path="spar-mobile/retailer-commissions"
          element={
            <PageProtectedRoute pageKey="spar-mobile">
              <SparMobilePage basePath="/spar-mobile" sectionName="retailer-commissions" />
            </PageProtectedRoute>
          }
        />
        <Route
          path="spar-mobile/expired-rewards"
          element={
            <PageProtectedRoute pageKey="spar-mobile">
              <SparMobilePage basePath="/spar-mobile" sectionName="expired-rewards" />
            </PageProtectedRoute>
          }
        />
        <Route
          path="spar-mobile/basket-search"
          element={
            <PageProtectedRoute pageKey="spar-mobile">
              <BasketSearchPage />
            </PageProtectedRoute>
          }
        />
        <Route
          path="spar-mobile/brevo-contacts-sync"
          element={
            <PageProtectedRoute pageKey="spar-mobile">
              <BrevoContactsSyncPage />
            </PageProtectedRoute>
          }
        />
        <Route
          path="spar-mobile/abuse-detection-reports"
          element={
            <PageProtectedRoute pageKey="spar-mobile">
              <AbuseDetectionReportsPage />
            </PageProtectedRoute>
          }
        />
        <Route
          path="spar-mobile/transfer-promo-data"
          element={
            <PageProtectedRoute pageKey="spar-mobile">
              <TransferPromoDataPage />
            </PageProtectedRoute>
          }
        />
        <Route
          path="spar-mobile/promo-attribution"
          element={
            <PageProtectedRoute pageKey="spar-mobile">
              <PromoAttributionOverviewPage />
            </PageProtectedRoute>
          }
        />
        <Route
          path="spar-mobile/promo-attribution/executive-summary"
          element={
            <PageProtectedRoute pageKey="spar-mobile">
              <PromoAttributionExecutiveSummaryPage />
            </PageProtectedRoute>
          }
        />
        <Route
          path="spar-mobile/promo-attribution/products"
          element={
            <PageProtectedRoute pageKey="spar-mobile">
              <PromoAttributionProductsPage />
            </PageProtectedRoute>
          }
        />
        <Route
          path="spar-mobile/promo-attribution/products/:gtin"
          element={
            <PageProtectedRoute pageKey="spar-mobile">
              <PromoAttributionProductDetailPage />
            </PageProtectedRoute>
          }
        />
        <Route
          path="spar-mobile/promo-attribution/rules"
          element={
            <PageProtectedRoute pageKey="spar-mobile">
              <PromoAttributionRulesPage />
            </PageProtectedRoute>
          }
        />
        <Route
          path="spar-mobile/promo-attribution/methodology"
          element={
            <PageProtectedRoute pageKey="spar-mobile">
              <PromoAttributionMethodologyPage />
            </PageProtectedRoute>
          }
        />
        <Route path="ai-sql" element={<PageProtectedRoute pageKey="ai-sql"><AiSqlPage /></PageProtectedRoute>} />
        <Route path="user-management" element={<PageProtectedRoute pageKey="user-management"><UserManagementPage /></PageProtectedRoute>} />
        <Route path="role-permissions" element={<PageProtectedRoute pageKey="user-management"><RolePermissionsPage /></PageProtectedRoute>} />
        <Route
          path="dev-tools"
          element={
            <PageProtectedRoute pageKey="dev-tools">
              <Navigate to="/dev-tools/column-to-csv" replace />
            </PageProtectedRoute>
          }
        />
        <Route
          path="dev-tools/column-to-csv"
          element={
            <PageProtectedRoute pageKey="dev-tools">
              <ColumnToCsvPage />
            </PageProtectedRoute>
          }
        />
        <Route
          path="dev-tools/csv-to-json"
          element={
            <PageProtectedRoute pageKey="dev-tools">
              <CsvToJsonPage />
            </PageProtectedRoute>
          }
        />
        <Route
          path="mtn"
          element={
            <PageProtectedRoute pageKey="mtn">
              <Navigate to="/mtn/billing-recon" replace />
            </PageProtectedRoute>
          }
        />
        <Route
          path="mtn/billing-recon"
          element={
            <PageProtectedRoute pageKey="mtn">
              <MtnBillingReconPage />
            </PageProtectedRoute>
          }
        />
        <Route
          path="mtn/invoices"
          element={
            <PageProtectedRoute pageKey="mtn">
              <MtnInvoicesPage />
            </PageProtectedRoute>
          }
        />
        <Route
          path="shopify"
          element={
            <PageProtectedRoute pageKey="shopify">
              <Navigate to="/shopify/stock-management" replace />
            </PageProtectedRoute>
          }
        />
        <Route
          path="shopify/stock-management"
          element={
            <PageProtectedRoute pageKey="shopify">
              <ShopifyStockManagementPage />
            </PageProtectedRoute>
          }
        />
        <Route
          path="shopify/monthly-billing"
          element={
            <PageProtectedRoute pageKey="shopify">
              <ShopifyMonthlyBillingPage />
            </PageProtectedRoute>
          }
        />
        <Route
          path="surveys"
          element={
            <PageProtectedRoute pageKey="surveys">
              <ManageSurveysPage />
            </PageProtectedRoute>
          }
        />
        <Route
          path="surveys/new"
          element={
            <PageProtectedRoute pageKey="surveys">
              <ErrorBoundary>
                <CreateEditSurveyPage />
              </ErrorBoundary>
            </PageProtectedRoute>
          }
        />
        <Route
          path="surveys/:id/edit"
          element={
            <PageProtectedRoute pageKey="surveys">
              <ErrorBoundary>
                <CreateEditSurveyPage />
              </ErrorBoundary>
            </PageProtectedRoute>
          }
        />
        <Route
          path="surveys/:id"
          element={
            <PageProtectedRoute pageKey="surveys">
              <SurveyDetailPage />
            </PageProtectedRoute>
          }
        />
        <Route
          path="reports"
          element={
            <PageProtectedRoute pageKey="reports">
              <Navigate to="/reports/spar-mobile-expo" replace />
            </PageProtectedRoute>
          }
        />
        <Route
          path="reports/spar-mobile-expo"
          element={
            <PageProtectedRoute pageKey="reports">
              <ReportsPage />
            </PageProtectedRoute>
          }
        />
        <Route
          path="xero"
          element={
            <PageProtectedRoute pageKey="xero">
              <Navigate to="/xero/contacts" replace />
            </PageProtectedRoute>
          }
        />
        <Route
          path="xero/contacts"
          element={
            <PageProtectedRoute pageKey="xero">
              <XeroContactsPage />
            </PageProtectedRoute>
          }
        />
        <Route
          path="xero/statements"
          element={
            <PageProtectedRoute pageKey="xero">
              <XeroStatementsPage />
            </PageProtectedRoute>
          }
        />
        <Route path="iot" element={<PageProtectedRoute pageKey="iot"><IotPage /></PageProtectedRoute>} />
        <Route path="iot/customer/:iCustomer" element={<PageProtectedRoute pageKey="iot"><IotCustomerAccountsPage /></PageProtectedRoute>} />
        <Route
          path="churn"
          element={
            <PageProtectedRoute pageKey="churn">
              <Navigate to="/churn/candidates" replace />
            </PageProtectedRoute>
          }
        />
        <Route
          path="churn/candidates"
          element={
            <PageProtectedRoute pageKey="churn">
              <ChurnPage />
            </PageProtectedRoute>
          }
        />
        <Route
          path="churn/failed-executions"
          element={
            <PageProtectedRoute pageKey="churn">
              <ChurnFailedExecutionsPage />
            </PageProtectedRoute>
          }
        />
        <Route
          path="vouchers"
          element={
            <PageProtectedRoute pageKey="vouchers">
              <Navigate to="/vouchers/ott-voucher" replace />
            </PageProtectedRoute>
          }
        />
        <Route
          path="vouchers/ott-voucher"
          element={
            <PageProtectedRoute pageKey="vouchers">
              <OttVoucherPage />
            </PageProtectedRoute>
          }
        />
        <Route path="*" element={<div style={{ padding: '32px' }}><h2 style={{ fontSize: '24px', fontWeight: 'bold' }}>Feature Coming Soon</h2><p style={{ marginTop: '8px' }}>This feature is being migrated.</p></div>} />
      </Route>
    </Routes>
  )
}

export default App
